import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Calculator,
  PieChart,
  BarChart3,
  Target,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Calendar,
  Package
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { formatDateForInput, createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import type { ProfitAnalysis, DateRange } from '@/types'

export default function ProfitAnalysisScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [profitAnalysis, setProfitAnalysis] = useState<ProfitAnalysis | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()

  const loadProfitAnalysis = async () => {
    if (!currentRestaurant?._id) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.getProfitAnalysis(currentRestaurant._id, dateRange)
      if (response.success) {
        setProfitAnalysis(response.data)
      } else {
        setError(response.message || 'Failed to load profit analysis')
      }
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load profit analysis'
        : 'Failed to load profit analysis'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadProfitAnalysis()
    }
  }, [currentRestaurant?._id])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    console.log(`📅 Profit Analysis date change - field: ${field}, value: ${value}`)

    setDateRange(prev => {
      const newDate = parseDate(value)
      const updatedRange = field === 'from'
        ? createDateRange(newDate, prev.to)
        : createDateRange(prev.from, newDate)

      console.log(`📅 Previous range:`, prev)
      console.log(`📅 Updated range:`, updatedRange)

      return updatedRange
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading profit analysis...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-2"
              onClick={loadProfitAnalysis}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!profitAnalysis) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No profit analysis data available for the selected period.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Profit Analysis</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Profit & cost analysis
          </p>
        </div>
        <Button onClick={loadProfitAnalysis} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Analysis Period Selector - Desktop Only */}
      <div className="hidden md:flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1">
          <DateRangeModal
            dateRange={dateRange}
            onDateChange={handleDateChange}
            onApply={loadProfitAnalysis}
            title="Profit Analysis Period"
            description="Select the date range for your profit analysis"
          />
        </div>
        <Button
          onClick={loadProfitAnalysis}
          className="sm:w-auto flex items-center justify-center gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Calculator className="h-4 w-4" />
              Analyze
            </>
          )}
        </Button>
      </div>

      {/* Key Profit Metrics */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(profitAnalysis.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              Total income generated
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(profitAnalysis.totalCost)}</div>
            <p className="text-xs text-muted-foreground">
              Total production cost from orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            {profitAnalysis.netProfit >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-xl sm:text-2xl font-bold ${profitAnalysis.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(profitAnalysis.netProfit)}
            </div>
            <p className="text-xs text-muted-foreground">
              Revenue minus costs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-xl sm:text-2xl font-bold ${profitAnalysis.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {profitAnalysis.profitMargin.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Profit as % of revenue
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Profit Margin Visual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Profit Breakdown</span>
          </CardTitle>
          <CardDescription>
            Visual representation of revenue vs actual production costs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 sm:space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-medium">Revenue</span>
                <span className="text-xs sm:text-sm font-bold">{formatCurrency(profitAnalysis.totalRevenue)}</span>
              </div>
              <Progress value={100} className="h-2 sm:h-3" />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-medium">Costs</span>
                <span className="text-xs sm:text-sm font-bold">{formatCurrency(profitAnalysis.totalCost)}</span>
              </div>
              <Progress 
                value={profitAnalysis.totalRevenue > 0 ? (profitAnalysis.totalCost / profitAnalysis.totalRevenue) * 100 : 0} 
                className="h-2 sm:h-3" 
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-medium">Net Profit</span>
                <span className={`text-xs sm:text-sm font-bold ${profitAnalysis.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(profitAnalysis.netProfit)}
                </span>
              </div>
              <Progress 
                value={profitAnalysis.totalRevenue > 0 ? Math.abs(profitAnalysis.netProfit / profitAnalysis.totalRevenue) * 100 : 0} 
                className={`h-2 sm:h-3 ${profitAnalysis.netProfit >= 0 ? '' : 'opacity-50'}`}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Item-wise Profit Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>Item Profitability</span>
          </CardTitle>
          <CardDescription>
            Profit analysis using actual cost of production from orders
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 sm:space-y-4">
            {profitAnalysis.itemAnalysis?.map((item, index: number) => (
              <div key={item.name} className="border rounded-lg p-3 sm:p-4">
                {/* Mobile Layout */}
                <div className="block lg:hidden space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm sm:text-base">{item.name}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground">
                        {item.category} • {item.quantitySold} units sold
                      </div>
                    </div>
                    <Badge 
                      variant={item.margin >= 20 ? 'default' : item.margin >= 10 ? 'secondary' : 'destructive'}
                      className="text-xs"
                    >
                      {item.margin.toFixed(1)}%
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 text-center text-xs sm:text-sm">
                    <div>
                      <div className="font-medium">{formatCurrency(item.price)}</div>
                      <div className="text-muted-foreground">Price</div>
                    </div>
                    <div>
                      <div className="font-medium">{formatCurrency(item.cost)}</div>
                      <div className="text-muted-foreground">Cost</div>
                    </div>
                    <div>
                      <div className={`font-medium ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(item.profit)}
                      </div>
                      <div className="text-muted-foreground">Profit</div>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden lg:flex lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {item.category} • {item.quantitySold} units sold
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 text-right text-sm">
                    <div>
                      <div className="font-medium">{formatCurrency(item.price)}</div>
                      <div className="text-muted-foreground">Price</div>
                    </div>
                    <div>
                      <div className="font-medium">{formatCurrency(item.cost)}</div>
                      <div className="text-muted-foreground">Cost</div>
                    </div>
                    <div>
                      <div className={`font-medium ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(item.profit)}
                      </div>
                      <div className="text-muted-foreground">Profit</div>
                    </div>
                    <div>
                      <Badge 
                        variant={item.margin >= 20 ? 'default' : item.margin >= 10 ? 'secondary' : 'destructive'}
                      >
                        {item.margin.toFixed(1)}%
                      </Badge>
                      <div className="text-muted-foreground text-xs mt-1">Margin</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Profit Analysis Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <PieChart className="h-5 w-5" />
            <span>Key Insights</span>
          </CardTitle>
          <CardDescription>
            Automated insights based on your profit data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
            <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                <span className="font-medium text-green-800 text-sm sm:text-base">Most Profitable Item</span>
              </div>
              <div className="mt-2">
                <div className="font-semibold text-green-900 text-sm sm:text-base">
                  {profitAnalysis.itemAnalysis?.[0]?.name || 'N/A'}
                </div>
                <div className="text-green-700 text-xs sm:text-sm">
                  {profitAnalysis.itemAnalysis?.[0]?.margin.toFixed(1)}% margin
                </div>
              </div>
            </div>

            <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Calculator className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                <span className="font-medium text-blue-800 text-sm sm:text-base">Overall Performance</span>
              </div>
              <div className="mt-2">
                <div className="font-semibold text-blue-900 text-sm sm:text-base">
                  {profitAnalysis.profitMargin >= 20 ? 'Excellent' : 
                   profitAnalysis.profitMargin >= 10 ? 'Good' : 
                   profitAnalysis.profitMargin >= 0 ? 'Fair' : 'Needs Attention'}
                </div>
                <div className="text-blue-700 text-xs sm:text-sm">
                  {profitAnalysis.profitMargin.toFixed(1)}% overall margin
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Floating Filter */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadProfitAnalysis}
        title="Profit Analysis Date Range"
        description="Select the date range for your profit analysis"
      />
    </div>
  )
}